import React from 'react';
import {
    Checkbox,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    DefaultButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import { ICrmFolioCandidate, IPsaProvisioningProposal } from '../../models/Extension';
import { CRM_SETUP_STRINGS } from './wizardStrings';

// The review of the LLM's proposed setup, shown only to wedge-sourced organizations.
//
// Every suggestion is a row with a checkbox and nothing is applied until the user confirms; the
// guardrail on this feature is that the proposal prefills the wizard and never writes anything
// itself. Rows the user unchecks are recorded as rejected, checked ones as accepted - that record
// is how anyone can later tell whether the guessing helps.
//
// Credentials are absent by construction: the API never proposes protected fields, and this
// component renders only what the proposal carries.

/** One reviewable suggestion. `key` doubles as the instrumentation key the API stores. */
export interface IProposalRow {
    key: string;
    label: string;
    value?: string;
    section: 'capability' | 'field' | 'slot';
}

/** What the user confirmed, in the shape the dialog needs to prefill its own state. */
export interface IProposalApplication {
    subExtensions: string[];
    sharedValues: { [field: string]: string };
    subFieldValues: { [identifier: string]: { [field: string]: string } };
    slots: { opportunity?: string; customerLookup?: string; locationLookup?: string };
    /** Per-row verdicts for the decision record. */
    fieldDecisions: { [key: string]: string };
    allAccepted: boolean;
}

export const buildProposalRows = (
    proposal: IPsaProvisioningProposal, candidate: ICrmFolioCandidate | undefined): IProposalRow[] => {
    const nameOf = (identifier: string) =>
        candidate?.capabilities?.find(c => c.identifier === identifier)?.name ?? identifier;

    const rows: IProposalRow[] = [];
    (proposal.proposedSubExtensions ?? []).forEach(identifier => rows.push({
        key: `capability.${identifier}`, label: nameOf(identifier), section: 'capability',
    }));
    Object.entries(proposal.proposedSharedFieldValues ?? {}).forEach(([field, value]) => rows.push({
        key: `shared.${field}`, label: field, value, section: 'field',
    }));
    Object.entries(proposal.proposedSubExtensionFieldValues ?? {}).forEach(([identifier, values]) =>
        Object.entries(values).forEach(([field, value]) => rows.push({
            key: `${identifier}.${field}`, label: `${nameOf(identifier)}: ${field}`, value, section: 'field',
        })));
    const slots: [string, string | undefined, string][] = [
        ['opportunity', proposal.proposedOpportunitySlot, CRM_SETUP_STRINGS.slotOpportunity],
        ['customerLookup', proposal.proposedCustomerLookupSlot, CRM_SETUP_STRINGS.slotCustomerLookup],
        ['locationLookup', proposal.proposedLocationLookupSlot, CRM_SETUP_STRINGS.slotLocationLookup],
    ];
    slots.forEach(([slot, identifier, label]) => {
        if (identifier) rows.push({
            key: `slot.${slot}`,
            label: CRM_SETUP_STRINGS.proposalSlotLabel(label, nameOf(identifier)),
            section: 'slot',
        });
    });
    return rows;
};

/** Turns the checked rows back into wizard state plus the per-row decision record. */
export const applySelection = (
    proposal: IPsaProvisioningProposal, rows: IProposalRow[], checked: Set<string>): IProposalApplication => {
    const fieldDecisions: { [key: string]: string } = {};
    rows.forEach(row => { fieldDecisions[row.key] = checked.has(row.key) ? 'accepted' : 'rejected'; });

    const subExtensions = (proposal.proposedSubExtensions ?? [])
        .filter(identifier => checked.has(`capability.${identifier}`));

    const sharedValues: { [field: string]: string } = {};
    Object.entries(proposal.proposedSharedFieldValues ?? {}).forEach(([field, value]) => {
        if (checked.has(`shared.${field}`)) sharedValues[field] = value;
    });

    const subFieldValues: { [identifier: string]: { [field: string]: string } } = {};
    Object.entries(proposal.proposedSubExtensionFieldValues ?? {}).forEach(([identifier, values]) => {
        const kept: { [field: string]: string } = {};
        Object.entries(values).forEach(([field, value]) => {
            if (checked.has(`${identifier}.${field}`)) kept[field] = value;
        });
        if (Object.keys(kept).length > 0) subFieldValues[identifier] = kept;
    });

    const slots: IProposalApplication['slots'] = {};
    if (proposal.proposedOpportunitySlot && checked.has('slot.opportunity')) slots.opportunity = proposal.proposedOpportunitySlot;
    if (proposal.proposedCustomerLookupSlot && checked.has('slot.customerLookup')) slots.customerLookup = proposal.proposedCustomerLookupSlot;
    if (proposal.proposedLocationLookupSlot && checked.has('slot.locationLookup')) slots.locationLookup = proposal.proposedLocationLookupSlot;

    return {
        subExtensions, sharedValues, subFieldValues, slots, fieldDecisions,
        allAccepted: rows.every(row => checked.has(row.key)),
    };
}

export interface ICrmProposalReviewStepProps {
    proposal: IPsaProvisioningProposal | undefined;
    candidate: ICrmFolioCandidate | undefined;
    pending: boolean;
    onApply: (application: IProposalApplication) => void;
    onSkip: () => void;
}

const SECTION_HEADINGS: Record<IProposalRow['section'], string> = {
    capability: CRM_SETUP_STRINGS.proposalCapabilitiesHeading,
    field: CRM_SETUP_STRINGS.proposalFieldsHeading,
    slot: CRM_SETUP_STRINGS.proposalSlotsHeading,
};

const CrmProposalReviewStep: React.FC<ICrmProposalReviewStepProps> = (props) => {
    const theme = useTheme();
    const rows = React.useMemo(
        () => props.proposal ? buildProposalRows(props.proposal, props.candidate) : [],
        [props.proposal, props.candidate]);
    // Everything starts checked: the point of the proposal is that reviewing a roughly-right setup
    // beats authoring one, and an uncheck is the meaningful action worth recording.
    const [checked, setChecked] = React.useState<Set<string>>(new Set());
    const seeded = React.useRef(false);
    React.useEffect(() => {
        if (seeded.current || rows.length === 0) return;
        seeded.current = true;
        setChecked(new Set(rows.map(row => row.key)));
    }, [rows]);

    const proposal = props.proposal;
    if (props.pending || !proposal) {
        return (
            <Stack tokens={{ childrenGap: '1em' }} horizontalAlign="center">
                <Spinner size={SpinnerSize.large} label={CRM_SETUP_STRINGS.proposalChecking} />
                <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {CRM_SETUP_STRINGS.proposalPendingDetail}
                </Text>
                <DefaultButton text={CRM_SETUP_STRINGS.proposalSkip} onClick={props.onSkip} />
            </Stack>
        );
    }

    const confidence = proposal.confidence;
    return (
        <Stack tokens={{ childrenGap: '1em' }}>
            <MessageBar messageBarType={MessageBarType.info}>{CRM_SETUP_STRINGS.proposalIntro}</MessageBar>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
                {CRM_SETUP_STRINGS.proposalHeading}
            </Text>
            {proposal.rationale && <Text variant="small">{proposal.rationale}</Text>}
            {confidence !== undefined && confidence !== null && (
                <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {CRM_SETUP_STRINGS.proposalConfidence(Math.round(confidence * 100))}
                </Text>
            )}

            {(['capability', 'field', 'slot'] as IProposalRow['section'][]).map(section => {
                const sectionRows = rows.filter(row => row.section === section);
                if (sectionRows.length === 0) return null;
                return (
                    <Stack key={section} tokens={{ childrenGap: '0.5em' }}>
                        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                            {SECTION_HEADINGS[section]}
                        </Text>
                        {sectionRows.map(row => (
                            <Checkbox
                                key={row.key}
                                label={row.value !== undefined ? `${row.label}: ${row.value}` : row.label}
                                checked={checked.has(row.key)}
                                onChange={(_e, on) => {
                                    const next = new Set(checked);
                                    if (on) next.add(row.key); else next.delete(row.key);
                                    setChecked(next);
                                }}
                            />
                        ))}
                    </Stack>
                );
            })}

            <Stack horizontal tokens={{ childrenGap: '0.5em' }}>
                <PrimaryButton
                    text={CRM_SETUP_STRINGS.proposalApply}
                    disabled={rows.length === 0}
                    onClick={() => props.onApply(applySelection(proposal, rows, checked))}
                />
                <DefaultButton text={CRM_SETUP_STRINGS.proposalSkip} onClick={props.onSkip} />
            </Stack>
        </Stack>
    );
};

export default CrmProposalReviewStep;
